import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DescriptionIcon from '@mui/icons-material/Description';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BusinessIcon from '@mui/icons-material/Business';

import { toast } from '@src/components/core/toaster';
import {
  listContractTemplates,
  generateContract,
  sendContract,
  listContracts,
} from '@src/api/actions/contracts';

const STATUS_COLORS = {
  draft: 'default',
  sent: 'info',
  viewed: 'warning',
  signed: 'success',
  voided: 'error',
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function ContractBuilder({ clientSlug, clientName, parentCompanyId, parentCompanyName }) {
  const [templates, setTemplates] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Generate form
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signerName, setSignerName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [useParentCompany, setUseParentCompany] = useState(false);

  // Preview
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContractId, setPreviewContractId] = useState(null);
  const [previewSending, setPreviewSending] = useState(false);

  // Send dialog
  const [sendDialog, setSendDialog] = useState(null);
  const [sendEmail, setSendEmail] = useState('');
  const [sending, setSending] = useState(false);

  const reloadContracts = async () => {
    const res = await listContracts(clientSlug, parentCompanyId);
    setContracts(res?.data?.results || []);
  };

  useEffect(() => {
    loadData();
  }, [clientSlug, parentCompanyId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tplRes, ctRes] = await Promise.all([
        listContractTemplates(),
        listContracts(clientSlug, parentCompanyId),
      ]);
      setTemplates(tplRes?.data?.results || []);
      setContracts(ctRes?.data?.results || []);
    } catch {
      toast.error('Failed to load contract data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedTemplate) {
      toast.error('Select a template');
      return;
    }
    setGenerating(true);
    try {
      const payload = {
        template_id: selectedTemplate,
        signer_email: signerEmail,
        signer_name: signerName,
      };
      if (useParentCompany && parentCompanyId) {
        payload.parent_company_id = parentCompanyId;
      } else {
        payload.client_slug = clientSlug;
      }
      const res = await generateContract(payload);
      const data = res?.data;
      toast.success('Contract generated!');
      setPreviewHtml(data?.preview_html || '');
      setPreviewContractId(data?.id || null);
      setPreviewOpen(true);
      await reloadContracts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate contract');
    } finally {
      setGenerating(false);
    }
  };

  const handleSend = async () => {
    if (!sendDialog) return;
    setSending(true);
    try {
      await sendContract(sendDialog, { signer_email: sendEmail || undefined });
      toast.success('Contract email sent!');
      setSendDialog(null);
      setSendEmail('');
      await reloadContracts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const copySignUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Signing link copied!');
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Generate new contract */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            <DescriptionIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 20 }} />
            Generate Contract
          </Typography>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Template"
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="">Select template...</MenuItem>
                {templates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name} v{t.version}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Signer Name"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Signer Email"
                type="email"
                value={signerEmail}
                onChange={(e) => setSignerEmail(e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleGenerate}
                disabled={!selectedTemplate || generating}
                sx={{ height: 40 }}
              >
                {generating ? <CircularProgress size={18} /> : 'Generate'}
              </Button>
            </Grid>
          </Grid>

          {parentCompanyId && (
            <Alert
              severity={useParentCompany ? 'warning' : 'info'}
              icon={<BusinessIcon />}
              sx={{ mt: 2, cursor: 'pointer' }}
              onClick={() => setUseParentCompany((v) => !v)}
              action={
                <Button color="inherit" size="small" onClick={(e) => { e.stopPropagation(); setUseParentCompany((v) => !v); }}>
                  {useParentCompany ? 'Switch to Client' : 'Switch to Parent'}
                </Button>
              }
            >
              {useParentCompany
                ? <>Generating for <strong>parent company: {parentCompanyName}</strong> (covers all child clients)</>
                : <>Generating for <strong>{clientName}</strong> only. Click to generate for parent company instead.</>
              }
            </Alert>
          )}

          {templates.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No contract templates found. Create one in the admin to get started.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Contracts list */}
      {contracts.length > 0 && (
        <Card>
          <CardContent sx={{ p: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ p: 2, pb: 0 }}>
              Contracts
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Signer</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Signed</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contracts.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      {c.name}
                      {c.parent_company_name && (
                        <Chip label="Parent Co." size="small" variant="outlined" color="secondary" sx={{ ml: 1 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{c.signer_name || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{c.signer_email || ''}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={c.sign_status} size="small" color={STATUS_COLORS[c.sign_status] || 'default'} />
                    </TableCell>
                    <TableCell>{fmtDate(c.signed_at)}</TableCell>
                    <TableCell>{fmtDate(c.uploaded_at)}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        {c.sign_status !== 'signed' && c.sign_status !== 'voided' && (
                          <Button
                            size="small"
                            startIcon={<SendIcon />}
                            onClick={() => {
                              setSendDialog(c.id);
                              setSendEmail(c.signer_email || '');
                            }}
                          >
                            Send
                          </Button>
                        )}
                        {c.sign_url && (
                          <Button
                            size="small"
                            startIcon={<ContentCopyIcon />}
                            onClick={() => copySignUrl(c.sign_url)}
                          >
                            Copy Link
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Preview dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Contract Preview</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              '& h1, & h2, & h3': { mt: 2, mb: 1 },
              '& p': { mb: 1, lineHeight: 1.7 },
            }}
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          {previewContractId && (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  const contract = contracts.find((c) => c.id === previewContractId);
                  if (contract?.sign_url) {
                    navigator.clipboard.writeText(contract.sign_url);
                    toast.success('Signing link copied!');
                  }
                }}
                startIcon={<ContentCopyIcon />}
              >
                Copy Signing Link
              </Button>
              <Button
                variant="contained"
                startIcon={previewSending ? <CircularProgress size={16} /> : <SendIcon />}
                disabled={previewSending || !signerEmail}
                onClick={async () => {
                  setPreviewSending(true);
                  try {
                    await sendContract(previewContractId, { signer_email: signerEmail || undefined });
                    toast.success('Contract sent to ' + signerEmail);
                    setPreviewOpen(false);
                    const ctRes = await listContracts(clientSlug);
                    setContracts(ctRes?.data?.results || []);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Failed to send');
                  } finally {
                    setPreviewSending(false);
                  }
                }}
              >
                {previewSending ? 'Sending...' : `Send to ${signerEmail || 'Client'}`}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Send dialog */}
      <Dialog open={!!sendDialog} onClose={() => setSendDialog(null)}>
        <DialogTitle>Send Contract for Signing</DialogTitle>
        <DialogContent>
          <TextField
            label="Signer Email"
            type="email"
            value={sendEmail}
            onChange={(e) => setSendEmail(e.target.value)}
            fullWidth
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialog(null)}>Cancel</Button>
          <Button onClick={handleSend} variant="contained" disabled={!sendEmail || sending}>
            {sending ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
